import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import permissionsService from "../services/permissionsService";
import styles from "./crmPermissionEdit.module.css";

const CrmPermissionEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permission, setPermission] = useState(null);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    route: "",
  });
  const [errors, setErrors] = useState({});

  // Typeahead state
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    loadPermissionData();
  }, [id]);

  const loadPermissionData = async () => {
    try {
      setLoading(true);
      const response = await permissionsService.getPermissionEdit(id);

      if (response.success) {
        const { permission, roles, directlyAddedUsers, departments } = response.data;
        setPermission(permission);
        setRoles(roles || []);
        setUsers(directlyAddedUsers || []);
        setDepartments(departments || []);

        setFormData({
          name: permission.name || "",
          display_name: permission.display_name || "",
          description: permission.description || "",
          route: permission.route || "",
        });
      } else {
        toast.error("Failed to load permission data");
      }
    } catch (error) {
      console.error("Error loading permission data:", error);
      toast.error("Error loading permission data");
    } finally {
      setLoading(false);
    }
  };

  // User search with debounce
  const searchUsers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setUserSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(
        `${baseUrl}/api/users/search/typeahead?q=${encodeURIComponent(searchTerm)}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserSearchResults(data.data || []);
          setShowDropdown(true);
        }
      }
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserSearchChange = (e) => {
    const value = e.target.value;
    setUserSearchTerm(value);
    setSelectedUserId(null);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(value);
    }, 300);
  };

  const handleUserSelect = async (user) => {
    setUserSearchTerm(user.label);
    setSelectedUserId(user.id);
    setShowDropdown(false);
    setUserSearchResults([]);

    // Add user to permission
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${baseUrl}/api/permissions/user/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          permission_id: id,
          user_id: user.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success('User added to permission successfully!');
          // Reload permission data to show updated user list
          await loadPermissionData();
          setUserSearchTerm('');
        } else {
          toast.error(data.message || 'Failed to add user');
        }
      } else {
        toast.error('Failed to add user to permission');
      }
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Error adding user to permission');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!formData.display_name.trim()) {
      newErrors.display_name = "Display name is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSaving(true);
      const response = await permissionsService.updatePermission(id, formData);

      if (response.success) {
        toast.success("Permission successfully updated");
        navigate("/permissions");
      } else {
        toast.error(response.message || "Failed to update permission");
      }
    } catch (error) {
      console.error("Error updating permission:", error);
      toast.error("Error updating permission");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
              <div className={styles.panelBody}>
                <div className={styles.textCenter}>
                  <div className={styles.spinnerBorder} role="status">
                    <span className={styles.srOnly}>Loading...</span>
                  </div>
                  <p>Loading permission data...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${styles.jsUsersTypeahead}`}>
      {/* Breadcrumb */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <ol className={styles.breadcrumb}>
            <li><a href="/dashboard">Dashboard</a></li>
            <li><a href="/permissions">Permissions</a></li>
            <li className={styles.active}>{permission?.name || "Edit permission"}</li>
          </ol>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          {/* Error Display */}
          {Object.keys(errors).length > 0 && (
            <div className={`${styles.alert} ${styles.alertDanger}`}>
              <strong>Whoops!</strong> There were some problems with your input.<br /><br />
              <ul>
                {Object.values(errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Edit Permission Form */}
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
            <div className={`${styles.panelHeading} ${styles.panelTitle}`}>
              <h3>Edit permission</h3>
            </div>
            <div className={`${styles.panelBody} ${styles.panelForm}`}>
              <form onSubmit={handleSubmit}>
                <fieldset className={styles.formHorizontal}>
                  <input type="hidden" name="permission_id" value={permission?.id} />

                  {/* Name Field */}
                  <div className={`${styles.formGroup} ${errors.name ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        disabled
                      />
                      {errors.name && <span className={styles.helpBlock}>{errors.name}</span>}
                    </div>
                  </div>

                  {/* Display Name Field */}
                  <div className={`${styles.formGroup} ${errors.display_name ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Display Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="display_name"
                        value={formData.display_name}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                      {errors.display_name && <span className={styles.helpBlock}>{errors.display_name}</span>}
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className={`${styles.formGroup} ${errors.description ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Description
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                      {errors.description && <span className={styles.helpBlock}>{errors.description}</span>}
                    </div>
                  </div>

                  {/* Route Field */}
                  <div className={`${styles.formGroup} ${errors.route ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Route
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="route"
                        value={formData.route}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        disabled
                      />
                      {errors.route && <span className={styles.helpBlock}>{errors.route}</span>}
                    </div>
                  </div>
                </fieldset>

                <fieldset className={styles.formHorizontal}>
                  <div className={`${styles.formGroup} ${styles.textRight}`}>
                    <div className={styles.colSm3}></div>
                    <div className={styles.colSm5}>
                      <button
                        type="submit"
                        className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                        disabled={saving}
                      >
                        <i className={styles.glyphicon}></i>{" "}
                        {saving ? "Saving..." : "Save permission"}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>

        {/* Roles Section */}
        <div className={styles.colMd6}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
            <div className={`${styles.panelHeading} ${styles.panelTitle}`}>Roles</div>
            <div className={`${styles.panelBody} ${styles.panelForm}`}>
              <table className={`${styles.table} ${styles.tableStriped} ${styles.tableHover} ${styles.tableCondensed} ${styles.contractorsDecisionTable}`}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role, index) => (
                    <tr key={role.id}>
                      <td>{index}</td>
                      <td>
                        <span className={`${styles.label} ${styles.labelDefault} ${styles.labelPermissionSystem}`}>
                          {role.name}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {roles.length === 0 && (
                    <tr>
                      <td colSpan="2" className={`${styles.textCenter} ${styles.textMuted}`}>
                        No roles assigned
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Users Section with Typeahead */}
        <div className={styles.colMd6}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
            <div className={`${styles.panelHeading} ${styles.panelTitle}`}>Users</div>
            <div className={`${styles.panelBody} ${styles.panelForm}`}>
              <div className={styles.formGroup}>
                <label className={`${styles.controlLabel} ${styles.colSm12} ${styles.labelRequired} ${styles.textLeft}`}>
                  Type user's email or name to add
                </label>
                <div className={styles.colSm12} style={{ position: 'relative' }}>
                  <input
                    className={`${styles.typeahead} ${styles.formControl}`}
                    id="user_title"
                    name="user_title"
                    type="text"
                    placeholder="Start typing name or email..."
                    style={{ textAlign: 'left' }}
                    value={userSearchTerm}
                    onChange={handleUserSearchChange}
                    autoComplete="off"
                  />
                  {isSearching && (
                    <div style={{ position: 'absolute', right: '10px', top: '10px' }}>
                      <span>Searching...</span>
                    </div>
                  )}
                  {showDropdown && userSearchResults.length > 0 && (
                    <ul style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      listStyle: 'none',
                      padding: 0,
                      margin: '2px 0 0 0',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      {userSearchResults.map(user => (
                        <li
                          key={user.id}
                          onClick={() => handleUserSelect(user)}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          <div><strong>{user.fullName}</strong></div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <table className={`${styles.table} ${styles.tableStriped} ${styles.tableHover} ${styles.tableCondensed} ${styles.contractorsDecisionTable}`}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(users) && users.length > 0 ? users.map((user, index) => (
                    <tr key={user.id}>
                      <td>{index + 1}</td>
                      <td>
                        <span className={`${styles.label} ${styles.labelDefault} ${styles.labelPermissionSystem}`}>
                          {user.fullName || `${user.first_name} ${user.last_name}`}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="2" className={`${styles.textCenter} ${styles.textMuted}`}>
                        No users assigned
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Departments Section */}
        <div className={styles.colMd6}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
            <div className={`${styles.panelHeading} ${styles.panelTitle}`}>Departments</div>
            <div className={`${styles.panelBody} ${styles.panelForm}`}>
              <table className={`${styles.table} ${styles.tableStriped} ${styles.tableHover} ${styles.tableCondensed} ${styles.contractorsDecisionTable}`}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Department</th>
                    <th>Color</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((department, index) => (
                    <tr key={department.id}>
                      <td>{index}</td>
                      <td>
                        <span className={`${styles.label} ${styles.labelDefault} ${styles.labelPermissionSystem}`}>
                          {department.name}
                        </span>
                      </td>
                      <td>
                        <span
                          className={styles.label}
                          style={{ backgroundColor: department.color }}
                        >
                          {department.color}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {departments.length === 0 && (
                    <tr>
                      <td colSpan="3" className={`${styles.textCenter} ${styles.textMuted}`}>
                        No departments assigned
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmPermissionEdit;
